import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Title,
    Text,
    Alert,
    Table,
    Paper,
    Button,
    Badge,
    Group,
} from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import ContentTypesCacheService, { ContentType } from '../../services/ContentTypesCacheService';
import { TABLE_SCROLL_MIN_WIDTH } from '../../theme/mantineTheme';
import { EmptyState } from '../common/EmptyState';
import { LoadingState } from '../common/LoadingState';

const ContentTypesTesting: React.FC = () => {
    const [contentTypes, setContentTypes] = useState<ContentType[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const cacheService = useMemo(() => ContentTypesCacheService.getInstance(), []);

    const fetchContentTypes = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await cacheService.getContentTypes();
            setContentTypes(data);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch content types';
            setError(errorMessage);
            console.error('Error fetching content types:', err);
            setContentTypes([]);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        cacheService.clearCache();
        await fetchContentTypes();
    };

    useEffect(() => {
        fetchContentTypes();
    }, []);

    return (
        <Box p="lg">
            <Group justify="space-between" align="center" mb="lg">
                <Title order={4} mb="xs">
                    Content Types Testing
                </Title>
                <Button
                    variant="outline"
                    leftSection={<IconRefresh size={18} />}
                    onClick={handleRefresh}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Group>

            <Text size="sm" c="dimmed" mb="lg">
                View content types used across entities in the system.
            </Text>

            {error && (
                <Alert color="red" mb="md">
                    {error}
                </Alert>
            )}

            {loading ? (
                <LoadingState variant="centered" />
            ) : (
                <Paper withBorder p="md">
                    {contentTypes.length === 0 ? (
                        <EmptyState title="No content types found" />
                    ) : (
                        <Table.ScrollContainer minWidth={TABLE_SCROLL_MIN_WIDTH}>
                            <Table withTableBorder stickyHeader>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th><strong>Content Type</strong></Table.Th>
                                        <Table.Th><strong>Extension</strong></Table.Th>
                                        <Table.Th><strong>Version</strong></Table.Th>
                                        <Table.Th><strong>ID</strong></Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {contentTypes.map((item, index) => (
                                        <Table.Tr key={index}>
                                            <Table.Td>
                                                <Badge variant="outline" color="blue">
                                                    {item.ContentType || 'N/A'}
                                                </Badge>
                                            </Table.Td>
                                            <Table.Td>
                                                <Badge variant="outline" color="gray" size="sm">
                                                    {item.Ext || 'N/A'}
                                                </Badge>
                                            </Table.Td>
                                            <Table.Td>
                                                <Text size="sm">
                                                    {item.Version || 'N/A'}
                                                </Text>
                                            </Table.Td>
                                            <Table.Td>
                                                <Text size="sm" ff="monospace" fz="xs">
                                                    {item.Id || 'N/A'}
                                                </Text>
                                            </Table.Td>
                                        </Table.Tr>
                                    ))}
                                </Table.Tbody>
                            </Table>
                        </Table.ScrollContainer>
                    )}
                </Paper>
            )}
        </Box>
    );
};

export default ContentTypesTesting;
